import { AfterViewInit, ChangeDetectorRef, Component, ComponentFactoryResolver, ComponentRef, ElementRef, Injector, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ConfirmDialogComponent } from '@components/dialog/confirm-dialog/confirm-dialog.component';
import { InputDialogComponent } from '@components/dialog/input-dialog/input-dialog.component';
import { Config, NavController, Platform } from '@ionic/angular';
import { ShowConfirmDialogArgs, ShowInputDialogArgs } from '@services/dialog/dialog-service.interfaces';
import { DialogService } from '@services/dialog/dialog.service';
import { LoadingService } from '@services/loading/loading.service';
import { PlatformService } from '@services/platform/platform.service';
import { StorageService } from '@services/storage/storage.service';
import { UserService } from '@services/user/user.service';
import { filter } from 'rxjs/operators';
import { register } from 'swiper/element/bundle';

register();

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit {

    // region Initialization

    @ViewChild('impersonationBanner') impersonationBannerRef: ElementRef<HTMLElement>;

    private isConfirmRoute = false;

    constructor(
        private injector: Injector,
        private platform: Platform,
        private config: Config,
        private router: Router,
        private navCtrl: NavController,
        private platformService: PlatformService,
        private loading: LoadingService,
        private dialog: DialogService,
        protected userService: UserService,
        private storage: StorageService,
        private changeDetectorRef: ChangeDetectorRef,
        private viewContainer: ViewContainerRef,
        private componentFactoryResolver: ComponentFactoryResolver
    ) {
        this.init();
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.isConfirmRoute = event.urlAfterRedirects.startsWith('/confirm');
            });
    }

    async init(): Promise<any> {
        await this.platform.ready();

        if (this.platformService.isMobile) {
            document.documentElement.classList.add('mobile');
        }

        await this.loading.init();
    }

    ngOnInit(): void {
        this.dialog.showConfirmDialog.subscribe(args => this.showConfirmDialog(ConfirmDialogComponent, args));
        this.dialog.showInputDialog.subscribe(args => this.showInputDialog(InputDialogComponent, args));

        if (this.isImpersonating) {
            document.body.classList.add('is-impersonating');

            // APP_INITIALIZER completed the token exchange before bootstrap;
            // consume the one-shot navigate flag it left behind.
            if (sessionStorage.getItem(StorageService.IMPERSONATION_NAVIGATE_KEY)) {
                sessionStorage.removeItem(StorageService.IMPERSONATION_NAVIGATE_KEY);
                this.navCtrl.navigateRoot(['/worksheets']);
            }
        }
    }

    // endregion

    // region Methods

    get isMobile(): boolean {
        return this.platformService.isMobile && !this.isConfirmRoute;
    }

    get isImpersonating(): boolean {
        return !!sessionStorage.getItem(StorageService.IMPERSONATION_MODE_KEY);
    }

    ngAfterViewInit(): void {
        if (this.impersonationBannerRef) {
            const h = this.impersonationBannerRef.nativeElement.getBoundingClientRect().height;
            document.documentElement.style.setProperty('--impersonation-banner-height', `${h}px`);
        }
    }

    exitImpersonation(): void {
        this.storage.clearImpersonationSession();
        window.close();
        // Fallback: if the tab wasn't opened via window.open() the close()
        // is a no-op, so navigate to login to leave a clean state.
        setTimeout(() => this.navCtrl.navigateRoot(['/login']), 100);
    }

    // Background grid helpers for the mobile blocker pattern
    bgCols = Array.from({ length: 8 });
    bgRows = Array.from({ length: 14 });

    showConfirmDialog(dialogComponent: new(dialog: DialogService) => ConfirmDialogComponent, args: ShowConfirmDialogArgs): ComponentRef<ConfirmDialogComponent> {
        this.viewContainer.clear();

        let dialogComponentFactory = this.componentFactoryResolver.resolveComponentFactory(dialogComponent);
        let dialogComponentRef = this.viewContainer.createComponent(dialogComponentFactory);

        dialogComponentRef.instance.init(args);

        dialogComponentRef.instance.close.subscribe(() => {
            dialogComponentRef.destroy();
        });

        return dialogComponentRef;
    }

    showInputDialog(dialogComponent: new(dialog: DialogService) => InputDialogComponent, args: ShowInputDialogArgs): ComponentRef<InputDialogComponent> {
        this.viewContainer.clear();

        let dialogComponentFactory = this.componentFactoryResolver.resolveComponentFactory(dialogComponent);
        let dialogComponentRef = this.viewContainer.createComponent(dialogComponentFactory);

        dialogComponentRef.instance.init(args);

        dialogComponentRef.instance.close.subscribe(() => {
            dialogComponentRef.destroy();
        });

        return dialogComponentRef;
    }

    // endregion
}
